import React, { memo, useState } from "react";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { FaBus, FaPlus, FaRegQuestionCircle, FaTaxi, FaTicketAlt, FaRegCommentDots, FaTimes } from "react-icons/fa";
import { Field, FieldArray } from "@progress/kendo-react-form";
import ShadowCard from "../../../components/common/ShadowCard";
import IconComponent from "../../../components/common/IconComponent";
import FormTextField from "../../../components/formFields/FormTextField";
import FormNumericTextField from "../../../components/formFields/FormNumericTextField";
import FormTextArea from "../../../components/formFields/FormTextArea";
import FormMultiSelectionFiled from "../../../components/formFields/FormMultiSelectionFiled";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { getAllActiveServiceLocation, getServiceLocationByID } from "../../servicelocation/services/serviceLocation.services";
import { getAllActiveTravelActivity, getTravelActivityByID } from "../../travelactivity/services/travelActivity.services";
import { getAllActiveVehicleTypes } from "../../vehicletype/services/vehicletype.services";
import { getAllActiveB2BServices } from "../../b2bservice/service/b2bservice.services";
import FormSelectionField from "../../../components/formFields/FormSelectionField";
import "../b2bmodule.scss";

interface TransportsAndActivitiesSectionProps {
  startDate?: any;
  nights?: number;
  values: any;
  valueGetter: (field: string) => any;
  onFormChange?: (name: string, event: any) => void;
  dayOptions: any[];
  transportTotal?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// LIVE TOTAL CALCULATOR
// Reads directly from valueGetter on every render tick so that even deeply
// nested FieldArray changes (ServiceGroups → Services → Subservices →
// DailyPrices → GivenPrice) are captured immediately.
// ─────────────────────────────────────────────────────────────────────────────
const computeLiveTransportTotal = (valueGetter: (f: string) => any) => {
  let transportTotal = 0;
  let activityTotal = 0;
  let extraTotal = 0;

  // ── 1. ServiceGroups ──────────────────────────────────────────────────────
  const serviceGroups: any[] = valueGetter("TransportsAndActivities.ServiceGroups") || [];

  serviceGroups.forEach((group: any, gIdx: number) => {
    const services: any[] = valueGetter(`TransportsAndActivities.ServiceGroups[${gIdx}].Services`) || group?.Services || [];

    services.forEach((srv: any, sIdx: number) => {
      const subservices: any[] =
        valueGetter(`TransportsAndActivities.ServiceGroups[${gIdx}].Services[${sIdx}].Subservices`) ||
        srv?.Subservices || [];

      let svcTotal = 0;
      subservices.forEach((sub: any, subIdx: number) => {
        const qty = parseFloat(
          valueGetter(`TransportsAndActivities.ServiceGroups[${gIdx}].Services[${sIdx}].Subservices[${subIdx}].Qty`) ??
          sub?.Qty
        ) || 0;

        const dailyPrices: any[] =
          valueGetter(`TransportsAndActivities.ServiceGroups[${gIdx}].Services[${sIdx}].Subservices[${subIdx}].DailyPrices`) ||
          sub?.DailyPrices || [];

        dailyPrices.forEach((dp: any, dpIdx: number) => {
          const livePrice =
            valueGetter(`TransportsAndActivities.ServiceGroups[${gIdx}].Services[${sIdx}].Subservices[${subIdx}].DailyPrices[${dpIdx}].GivenPrice`) ??
            dp?.GivenPrice;

          svcTotal += (qty * (parseFloat(livePrice) || 0));
        });
      });

      const type = (srv.Type || "").toUpperCase();
      if (type === "ACTIVITY") activityTotal += svcTotal;
      else transportTotal += svcTotal;
    });
  });

  // ── 2. ExtraTransportationServices ───────────────────────────────────────
  const extraServices: any[] = valueGetter("TransportsAndActivities.ExtraTransportationServices") || [];
  extraServices.forEach((e: any, eIdx: number) => {
    const livePrice =
      valueGetter(`TransportsAndActivities.ExtraTransportationServices[${eIdx}].TotalPrice`) ??
      e?.TotalPrice;
    extraTotal += parseFloat(livePrice) || 0;
  });

  return {
    transportTotal,
    activityTotal,
    extraTotal,
    overallTotal: transportTotal + activityTotal + extraTotal
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// Hook: useLiveTransportTotal
// Re-computes total on every render. Because QuotationFormBody re-renders on
// every form change (Kendo Form behaviour), this naturally stays in sync.
// As an extra safety net we also use a 300 ms interval to catch any edge cases
// where the parent render doesn't propagate (e.g., rapid typing debounce).
// ─────────────────────────────────────────────────────────────────────────────
const useLiveTransportTotal = (valueGetter: (f: string) => any) => {
  const [totals, setTotals] = React.useState({
    transportTotal: 0,
    activityTotal: 0,
    extraTotal: 0,
    overallTotal: 0
  });

  // Recompute only when needed via interval or direct render if possible.
  // We removed the dependency-less useLayoutEffect here to prevent infinite loops.

  // Safety-net interval — catches debounced numeric input updates
  React.useEffect(() => {
    const id = setInterval(() => {
      setTotals(prev => {
        const next = computeLiveTransportTotal(valueGetter);
        if (
          prev.transportTotal !== next.transportTotal ||
          prev.activityTotal !== next.activityTotal ||
          prev.extraTotal !== next.extraTotal
        ) {
          return next;
        }
        return prev;
      });
    }, 300);
    return () => clearInterval(id);
  }, [valueGetter]);

  return totals;
};

// ─────────────────────────────────────────────────────────────────────────────
// SubservicePricingTable
// ─────────────────────────────────────────────────────────────────────────────
const SubservicePricingTable = (props: any) => {
  const {
    value: subservices = [],
    onPush,
    onRemove,
    namePrefix,
    stayDays = [],
    serviceType,
    activeVehicleTypes,
    activityTouristTypes,
    onFormChange,
    valueGetter
  } = props;

  return (
    <div className="table-responsive border rounded-3 overflow-hidden shadow-sm bg-white mt-1 mb-2">
      <table className="table table-sm table-hover mb-0 align-middle">
        <thead className="pricing-table-head">
          <tr>
            <th className="ps-3" style={{ width: "160px" }}>Type</th>
            <th className="text-center" style={{ width: "60px" }}>Qty.</th>
            {stayDays.length > 1 && <th className="text-center" style={{ width: "100px" }}>Travel Date</th>}
            <th className="text-center" style={{ width: "80px" }}>Rate</th>
            <th className="text-start px-3" style={{ width: "110px" }}>Given Price</th>
            <th className="pe-3" style={{ width: "40px" }}></th>
          </tr>
        </thead>
        <tbody>
          {(subservices || []).map((sub: any, sIdx: number) => {
            const subName = `${namePrefix}.Subservices[${sIdx}]`;
            const dailyPrices = sub?.DailyPrices || [];

            return dailyPrices.map((dp: any, dpIdx: number) => (
              <tr key={`${sIdx}-${dpIdx}`}>
                {dpIdx === 0 && (
                  <>
                    <td className="py-3 ps-3 bg-light-subtle border-end border-light" rowSpan={dailyPrices.length}>
                      <div className="d-flex align-items-center gap-2">
                        <Field
                          name={`${subName}.TypeID`}
                          wrapperClassName="m-0 flex-grow-1"
                          placeholder="Select Type"
                          options={
                            serviceType === "TRANSPORT"
                              ? (activeVehicleTypes || []).map((v: any) => ({ label: v.VehicleType, value: v.ID }))
                              : (activityTouristTypes || [])
                          }
                          component={FormSelectionField}
                          onChange={(e: any) => {
                            const val = e.value;
                            onFormChange(`${subName}.TypeID`, { value: val });
                            const opts = serviceType === "TRANSPORT"
                              ? (activeVehicleTypes || []).map((v: any) => ({ label: v.VehicleType, value: v.ID }))
                              : (activityTouristTypes || []);
                            const opt = opts.find((o: any) => o.value === val);
                            if (opt) onFormChange(`${subName}.TypeName`, { value: opt.label });
                          }}
                        />
                        {/* <IconComponent icon={FaSyncAlt} className="text-muted opacity-25" style={{ fontSize: "10px" }} title="Sync type across days" /> */}
                      </div>
                    </td>
                    <td className="py-3 text-center border-end border-light" rowSpan={dailyPrices.length}>
                      <Field
                        name={`${subName}.Qty`}
                        wrapperClassName="m-0 px-2"
                        component={FormNumericTextField}
                      />
                    </td>
                  </>
                )}
                {stayDays.length > 1 && (
                  <td className="py-2 text-center text-muted" style={{ fontSize: "12px" }}>
                    {moment(dp.Date).format("DD MMM")}
                  </td>
                )}
                <td className="py-2 text-center text-muted">
                  <div className="d-flex align-items-center justify-content-center gap-1" style={{ fontSize: "11px" }}>
                    <span className="opacity-50" style={{ fontSize: "9px" }}>INR</span>
                    <span className="fw-bold">N/A</span>
                  </div>
                </td>
                <td className="py-2 ps-3">
                  {/* ✅ This field name is the exact path computeLiveTransportTotal reads */}
                  <Field
                    name={`${subName}.DailyPrices[${dpIdx}].GivenPrice`}
                    wrapperClassName="m-0"
                    style={{ width: "80px" }}
                    component={FormNumericTextField}
                  />
                  {valueGetter && (
                    <>
                      {(() => {
                        const liveQty = parseFloat(valueGetter(`${subName}.Qty`)) || 0;
                        const livePrice = parseFloat(valueGetter(`${subName}.DailyPrices[${dpIdx}].GivenPrice`)) || 0;

                        if (liveQty > 1) {
                          return (
                            <div className="mt-1 small text-muted" style={{ fontSize: "12px" }}>
                              <span className="fw-bold text-dark">x {liveQty}</span> = {(liveQty * livePrice).toLocaleString()}
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </>
                  )}
                </td>
                {dpIdx === 0 && (
                  <td className="py-2 pe-3 text-end" rowSpan={dailyPrices.length}>
                    <div
                      className="b2b-remove-icon shadow-sm"
                      onClick={() => onRemove({ index: sIdx })}
                      title="Remove Sub-service"
                    >
                      <IconComponent icon={FaTimes} style={{ fontSize: "11px" }} />
                    </div>
                  </td>
                )}
              </tr>
            ));
          })}
        </tbody>
      </table>
      <div
        className="mt-2 mb-3 text-primary small cursor-pointer d-inline-flex align-items-center gap-2 fw-bold px-2 py-1 rounded hover-bg-light"
        onClick={() => {
          const newItemDays = stayDays.map((d: any) => ({ Date: d.Date, Rate: 0, GivenPrice: 0 }));
          onPush({ value: { TypeID: "", Qty: 1, DailyPrices: newItemDays } });
        }}
      >
        <IconComponent icon={FaPlus} style={{ fontSize: "10px" }} /> Add More Items
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// StayDaysField
// ─────────────────────────────────────────────────────────────────────────────
const StayDaysField = (props: any) => {
  const { value, onChange, namePrefix, onFormChange, services = [], ...others } = props;
  const primitiveValue = (value || []).map((v: any) => v?.Day).filter((v: any) => v !== undefined);

  return (
    <FormMultiSelectionFiled
      {...others}
      value={primitiveValue}
      onChange={(e: any) => {
        const selectedDayNums = e.value || [];
        const newStayDays = selectedDayNums.map((dNum: any) => {
          const opt = props.options.find((o: any) => o.value === dNum);
          return { Day: dNum, Date: opt?.date };
        });

        const updatedServices = (services || []).map((srv: any) => {
          const updatedSubservices = (srv.Subservices || []).map((sub: any) => {
            const existingDaily = sub.DailyPrices || [];
            const newDaily = newStayDays.map((newDay: any) => {
              const existing = existingDaily.find((ed: any) => ed.Date === newDay.Date);
              return existing || { Date: newDay.Date, Rate: 0, GivenPrice: 0 };
            });
            return { ...sub, DailyPrices: newDaily };
          });
          return { ...srv, Subservices: updatedSubservices };
        });

        onChange({ value: newStayDays });
        if (onFormChange) {
          onFormChange(`${namePrefix}.Services`, { value: updatedServices });
        }
      }}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SingleServiceItem
// ─────────────────────────────────────────────────────────────────────────────
const SingleServiceItem = (props: any) => {
  const {
    namePrefix,
    stayDays,
    serviceType,
    onRemove,
    isLast,
    activeServiceLocations,
    activeTravelActivities,
    activeVehicleTypes,
    activeB2BServices,
    onFormChange,
    srv,
  } = props;
  const dispatch = useAppDispatch();
  const [showComment, setShowComment] = useState(!!srv?.Comment);
  const [serviceInfoOptions, setServiceInfoOptions] = useState<any[]>([]);
  const [activityDetailsOptions, setActivityDetailsOptions] = useState<any[]>([]);
  const [activitySlots, setActivitySlots] = useState<any[]>([]);
  const [activityTouristTypes, setActivityTouristTypes] = useState<any[]>([]);
  const [currentActivity, setCurrentActivity] = useState<any>(null);

  // Hydration logic for Edit Mode
  React.useEffect(() => {
    const hydrate = async () => {
      const sId = srv?.ServiceID;
      const stId = srv?.ServiceTypeID;

      if (sId) {
        if (serviceType === "TRANSPORT") {
          const action: any = await dispatch(getServiceLocationByID(sId));
          const serviceLoc = action.payload;
          if (serviceLoc?.ServiceInfo) {
            const opts = serviceLoc.ServiceInfo.map((si: any) => ({ label: si.ServiceName, value: si.ID }));
            setServiceInfoOptions(opts);
            if (onFormChange) {
              const loc = activeServiceLocations.find((l: any) => l.ID == sId);
              if (loc) onFormChange(`${namePrefix}.ServiceName`, { value: loc.CityName });

              // ✅ NEW: Hydrate ServiceTypeName for TRANSPORT
              if (stId) {
                const info = opts.find((o: any) => o.value == stId);
                if (info) onFormChange(`${namePrefix}.ServiceTypeName`, { value: info.label });
              }
            }
          }
        } else if (serviceType === "ACTIVITY") {
          const action: any = await dispatch(getTravelActivityByID(sId));
          const activity = action.payload;
          setCurrentActivity(activity);
          if (activity?.ActivityDetails) {
            setActivityDetailsOptions(activity.ActivityDetails.map((ad: any) => ({ label: ad.Name, value: ad.ID })));
            if (onFormChange) {
              onFormChange(`${namePrefix}.ServiceName`, { value: activity.Name });
            }
            // If we also have a ServiceTypeID (TicketType), populate slots
            if (stId) {
              const detail = activity.ActivityDetails.find((ad: any) => ad.ID === stId);
              if (detail?.Slots) {
                const slotsArr = detail.Slots.split(",").map((s: string) => s.trim());
                setActivitySlots(slotsArr.map((s: string) => ({ label: s, value: s })));
              }
            }
          }
          if (activity?.TouristType) {
            const typesArr = activity.TouristType.split(",").map((s: string) => s.trim());
            const typesOptions = typesArr.map((s: string) => ({ label: s, value: s }));
            setActivityTouristTypes(typesOptions);

            // ✅ FIX: If the saved TypeID is a number (index), convert it to the string value
            // so it matches the dropdown options. 
            const subservicesPath = `${namePrefix}.Subservices`;
            const currentSubservices = srv?.Subservices || [];
            let hasTypeUpdate = false;
            const updatedSubservices = currentSubservices.map((sub: any) => {
              if (typeof sub.TypeID === "number" || (typeof sub.TypeID === "string" && !isNaN(Number(sub.TypeID)) && !typesArr.includes(sub.TypeID))) {
                const index = Number(sub.TypeID);
                if (index >= 0 && index < typesArr.length) {
                  hasTypeUpdate = true;
                  return { ...sub, TypeID: typesArr[index] };
                }
              }
              return sub;
            });

            if (hasTypeUpdate && onFormChange) {
              onFormChange(subservicesPath, { value: updatedSubservices });
            }
          }
        }
      }
    };
    hydrate();
  }, [srv?.ServiceID, srv?.ServiceTypeID, serviceType, dispatch]);

  const locationOptions = (activeServiceLocations || []).map((l: any) => ({ label: l.CityName, value: l.ID }));
  const travelActivityOptions = (activeTravelActivities || []).map((a: any) => ({ label: a.Name, value: a.ID }));

  const handleServiceChange = async (e: any) => {
    const locId = e.value;
    if (locId) {
      try {
        const action: any = await dispatch(getServiceLocationByID(locId));
        const serviceLoc = action.payload;
        setServiceInfoOptions(
          serviceLoc?.ServiceInfo
            ? serviceLoc.ServiceInfo.map((si: any) => ({ label: si.ServiceName, value: si.ID }))
            : []
        );
        if (onFormChange) {
          const loc = activeServiceLocations.find((l: any) => l.ID === locId);
          if (loc) onFormChange(`${namePrefix}.ServiceName`, { value: loc.CityName });
        }
      } catch (err) {
        console.error("Error fetching service location info:", err);
      }
    }
  };

  const handleActivityChange = async (e: any) => {
    const activityId = e.value;
    if (activityId) {
      try {
        const action: any = await dispatch(getTravelActivityByID(activityId));
        const activity = action.payload;
        setCurrentActivity(activity);
        if (activity?.ActivityDetails) {
          setActivityDetailsOptions(activity.ActivityDetails.map((ad: any) => ({ label: ad.Name, value: ad.ID })));
        }
        if (activity?.TouristType) {
          const typesArr = activity.TouristType.split(",").map((s: string) => s.trim());
          setActivityTouristTypes(typesArr.map((s: string) => ({ label: s, value: s })));
        }
        setActivitySlots([]);
        if (onFormChange) {
          onFormChange(`${namePrefix}.ServiceName`, { value: activity.Name });
          onFormChange(`${namePrefix}.ServiceTypeID`, { value: "" });
          onFormChange(`${namePrefix}.ServiceTypeName`, { value: "" });
          onFormChange(`${namePrefix}.DurationMinutes`, { value: "" });
          onFormChange(`${namePrefix}.Slot`, { value: "" });
        }
      } catch (err) {
        console.error("Error fetching activity details:", err);
      }
    } else {
      setCurrentActivity(null);
      setActivityDetailsOptions([]);
      setActivityTouristTypes([]);
      setActivitySlots([]);
    }
  };

  const handleServiceTypeChange = (e: any) => {
    const serviceTypeId = e.value;
    if (serviceType === "ACTIVITY" && currentActivity?.ActivityDetails) {
      const selectedDetail = currentActivity.ActivityDetails.find((ad: any) => ad.ID == serviceTypeId);
      if (selectedDetail) {

        if (onFormChange) {
          onFormChange(`${namePrefix}.ServiceTypeName`, { value: selectedDetail.Name });
        }
        if (selectedDetail.Duration) {
          const rawDuration = Number(selectedDetail.Duration) || 0;
          const durationType = selectedDetail.DurationType || "Mins";
          let calculatedMinutes = rawDuration;

          if (durationType === "Hrs") {
            calculatedMinutes = rawDuration * 60;
          } else if (durationType === "Days") {
            calculatedMinutes = rawDuration * 1440;
          }

          onFormChange(`${namePrefix}.DurationMinutes`, { value: `${calculatedMinutes}` });
        }
        if (selectedDetail.Slots) {
          const slotsArr = selectedDetail.Slots.split(",").map((s: string) => s.trim());
          setActivitySlots(slotsArr.map((s: string) => ({ label: s, value: s })));
        } else {
          setActivitySlots([]);
        }
      }
    } else if (serviceType === "TRANSPORT" && serviceInfoOptions.length > 0) {
      const selectedInfo = serviceInfoOptions.find((si: any) => si.value == serviceTypeId);
      if (selectedInfo && onFormChange) {
        onFormChange(`${namePrefix}.ServiceTypeName`, { value: selectedInfo.label });
      }
    }
  };

  return (
    <div className={`service-item-row bg-white ${!isLast ? "mb-4 pb-4 border-bottom border-light-subtle" : "mb-2"}`}>
      <div className="row g-4 align-items-start">
        <div className="col-12 col-lg-5 pe-lg-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className={`service-type-header ${serviceType === "ACTIVITY" ? 'activity' : 'transport'}`}>
              <IconComponent icon={serviceType === "ACTIVITY" ? FaTicketAlt : FaTaxi} className="opacity-75" />
              <span className="label-link">
                {serviceType === "ACTIVITY" ? "Activity/Ticket" : "Transport Service"}
              </span>
            </div>
            <div className="b2b-remove-icon shadow-sm" onClick={onRemove}>
              <IconComponent icon={FaTimes} style={{ fontSize: "10px" }} />
            </div>
          </div>

          <div className="mb-3">
            <Field
              name={`${namePrefix}.ServiceID`}
              label={serviceType === "ACTIVITY" ? "Name" : "Service Locations"}
              placeholder="Select Option"
              options={serviceType === "ACTIVITY" ? travelActivityOptions : locationOptions}
              component={FormSelectionField}
              onChange={serviceType === "ACTIVITY" ? handleActivityChange : handleServiceChange}
            />
          </div>

          <div className="row g-2 align-items-end">
            <div className={serviceType === "ACTIVITY" ? "col-12" : "col-9"}>
              <Field
                name={`${namePrefix}.ServiceTypeID`}
                label={serviceType === "ACTIVITY" ? "Ticket/Package Type" : "Service Type"}
                placeholder="Select Type"
                options={serviceType === "ACTIVITY" ? activityDetailsOptions : serviceInfoOptions}
                component={FormSelectionField}
                onChange={handleServiceTypeChange}
              />
            </div>
            {serviceType === "ACTIVITY" && (
              <>
                <div className="col-6">
                  <Field name={`${namePrefix}.Slot`} label="Slot" options={activitySlots} component={FormSelectionField} />
                </div>
                <div className="col-6">
                  <Field name={`${namePrefix}.DurationMinutes`} label="Duration (Minutes)" placeholder="1400 Mins" component={FormNumericTextField} />
                </div>
              </>
            )}
          </div>
        </div>

        <div className="col-12 col-lg-7 ps-lg-4 border-start border-light-subtle">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="fw-bold text-dark" style={{ fontSize: "13px" }}>Transportation and Prices</span>
            <div className="d-flex gap-3 align-items-center">
              <div
                className={`small text-muted d-flex align-items-center gap-1 cursor-pointer ${showComment ? "text-primary fw-bold" : ""}`}
                onClick={() => setShowComment(!showComment)}
              >
                <IconComponent icon={FaRegCommentDots} style={{ fontSize: "12px" }} />
                <span style={{ fontSize: "11px" }}>Comment</span>
              </div>
              {/* <IconComponent icon={FaSyncAlt} className="text-muted opacity-50 cursor-pointer" style={{ fontSize: "12px" }} /> */}
            </div>
          </div>

          <FieldArray
            name={`${namePrefix}.Subservices`}
            component={SubservicePricingTable}
            namePrefix={namePrefix}
            stayDays={stayDays}
            serviceType={serviceType}
            activityTouristTypes={activityTouristTypes}
            activeVehicleTypes={activeVehicleTypes}
            activeB2BServices={activeB2BServices}
            onFormChange={onFormChange}
            valueGetter={props.valueGetter}
          />

          {showComment && (
            <div className="mt-4">
              <Field name={`${namePrefix}.Comment`} component={FormTextArea} rows={2} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ServicesItemsList
// ─────────────────────────────────────────────────────────────────────────────
const ServicesItemsList = (props: any) => {
  const { value: services = [], onRemove, stayDays, activeServiceLocations, activeTravelActivities, activeB2BServices, activeVehicleTypes } = props;

  return (
    <React.Fragment>
      {(services || []).map((srv: any, sIdx: number) => (
        <SingleServiceItem
          key={sIdx}
          srv={srv}
          namePrefix={`${props.namePrefix}.Services[${sIdx}]`}
          stayDays={stayDays}
          serviceType={srv.Type}
          onRemove={() => onRemove({ index: sIdx })}
          isLast={sIdx === services.length - 1}
          activeServiceLocations={activeServiceLocations}
          activeTravelActivities={activeTravelActivities}
          activeB2BServices={activeB2BServices}
          activeVehicleTypes={activeVehicleTypes}
          onFormChange={props.onFormChange}
          valueGetter={props.valueGetter}
        />
      ))}
    </React.Fragment>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ServiceGroupCard
// ─────────────────────────────────────────────────────────────────────────────
const ServiceGroupCard = (props: any) => {
  const {
    namePrefix, onRemove, dayOptions, onFormChange, onAddService, onAddNextDay,
    isAllDaysSelected, activeServiceLocations, activeTravelActivities, activeB2BServices, activeVehicleTypes,
  } = props;

  return (
    <div className="service-group-group mb-5 pb-5">
      <div className="row g-4">
        <div className="col-12 col-lg-2 border-end border-light-subtle">
          <div className="days-selection-sidebar">
            <div className="fw-bold text-dark mb-2" style={{ fontSize: "13px" }}>Days Selection</div>
            <Field
              name={`${namePrefix}.StayDays`}
              placeholder="Select day(s)..."
              component={StayDaysField}
              options={dayOptions}
              namePrefix={namePrefix}
              onFormChange={onFormChange}
              services={props.value.Services}
            />
          </div>
        </div>

        <div className="col-12 col-lg-10">
          <FieldArray
            name={`${namePrefix}.Services`}
            component={ServicesItemsList}
            stayDays={props.value.StayDays}
            namePrefix={namePrefix}
            activeServiceLocations={activeServiceLocations}
            activeTravelActivities={activeTravelActivities}
            activeB2BServices={activeB2BServices}
            activeVehicleTypes={activeVehicleTypes}
            onFormChange={props.onFormChange}
            valueGetter={props.valueGetter}
          />

          <div className="d-flex justify-content-between align-items-center mt-3 pt-4 border-top border-light-subtle">
            <div className="d-flex gap-2">
              <Button type="button" fillMode="outline" themeColor="primary" size="small" className="fw-bold" onClick={() => onAddService("TRANSPORT")}>
                <IconComponent icon={FaPlus} className="me-1" /> Transport Service
              </Button>
              <Button type="button" fillMode="outline" themeColor="primary" size="small" className="fw-bold" onClick={() => onAddService("ACTIVITY")}>
                <IconComponent icon={FaPlus} className="me-1" /> Activity/Ticket
              </Button>
            </div>
            <div className="d-flex align-items-center gap-3">
              {!isAllDaysSelected && (
                <Button type="button" fillMode="outline" themeColor="primary" size="small" className="fw-bold px-3" onClick={onAddNextDay}>
                  <IconComponent icon={FaPlus} className="me-1" /> Next Day
                </Button>
              )}
              {props.groupsCount > 1 && (
                <div className="text-danger small cursor-pointer d-flex align-items-center gap-1 fw-bold" onClick={onRemove}>
                  <IconComponent icon={FaTimes} style={{ fontSize: "12px" }} /> Remove
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ServiceGroupsList
// ─────────────────────────────────────────────────────────────────────────────
const ServiceGroupsList = (props: any) => {
  const { value = [], onRemove, onPush, dayOptions, onFormChange, activeServiceLocations, activeTravelActivities, activeB2BServices, activeVehicleTypes } = props;

  const allSelectedDays = new Set<number>();
  (value || []).forEach((g: any) => {
    (g.StayDays || []).forEach((sd: any) => { if (sd?.Day !== undefined) allSelectedDays.add(sd.Day); });
  });
  const allDaysSelected = dayOptions.length > 0 && Array.from({ length: dayOptions.length }, (_, i) => i + 1).every(d => allSelectedDays.has(d));

  const createService = (type: "TRANSPORT" | "ACTIVITY", stayDays: any[] = []) => {
    const dailyPrices = stayDays.map(sd => ({ Date: sd.Date, Rate: 0, GivenPrice: 0 }));
    return { Type: type, ServiceID: '', ServiceTypeID: '', Slot: '', DurationMinutes: 0, Comment: '', Subservices: [{ TypeID: "", Qty: 1, DailyPrices: dailyPrices }] };
  };

  const handleAddNextDay = () => {
    let maxDay = 0;
    (value || []).forEach((g: any) => { (g.StayDays || []).forEach((sd: any) => { if (sd.Day > maxDay) maxDay = sd.Day; }); });
    const nextDay = maxDay + 1;
    const nextDayOpt = dayOptions.find((o: any) => o.value === nextDay);
    const newStayDays = nextDayOpt ? [{ Day: nextDay, Date: nextDayOpt.date }] : [];
    onPush({ value: { StayDays: newStayDays, Services: [createService("TRANSPORT", newStayDays)] } });
  };

  return (
    <div className="service-groups-list">
      {(value || []).map((group: any, gIdx: number) => {
        const namePrefix = `TransportsAndActivities.ServiceGroups[${gIdx}]`;
        if (!group) return null;
        return (
          <ServiceGroupCard
            key={gIdx}
            namePrefix={namePrefix}
            value={group}
            onRemove={() => onRemove({ index: gIdx })}
            dayOptions={dayOptions}
            onFormChange={onFormChange}
            isAllDaysSelected={allDaysSelected}
            groupsCount={value.length}
            activeServiceLocations={activeServiceLocations}
            activeTravelActivities={activeTravelActivities}
            activeB2BServices={activeB2BServices}
            activeVehicleTypes={activeVehicleTypes}
            valueGetter={props.valueGetter}
            onAddService={(type: any) => {
              const updatedServices = [...(group.Services || []), createService(type, group.StayDays)];
              onFormChange(`${namePrefix}.Services`, { value: updatedServices });
            }}
            onAddNextDay={handleAddNextDay}
          />
        );
      })}

      {(value || []).length === 0 && (
        <div className="text-center p-5 border rounded bg-light mb-4">
          <div className="text-muted mb-3">No service groups available for this itinerary.</div>
          <Button type="button" themeColor="primary" fillMode="solid" onClick={handleAddNextDay}>
            <IconComponent icon={FaPlus} className="me-2" /> Add First Day Group
          </Button>
        </div>
      )}

      <div className="mt-4">
        <Button type="button" themeColor="primary" fillMode="solid" className="fw-bold px-4" onClick={handleAddNextDay}>
          <IconComponent icon={FaPlus} className="me-2" /> Add More Services
        </Button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ExtraServicesList
// ─────────────────────────────────────────────────────────────────────────────
const ExtraServicesList = (props: any) => {
  const { value, onRemove, onPush, activeB2BServices, dayOptions } = props;
  const b2bServiceOptions = (activeB2BServices || []).map((s: any) => ({ label: s.ServiceName, value: s.ID }));

  return (
    <div className="extra-services-wrapper px-0">
      {(value || []).map((item: any, i: number) => {
        if (!item) return null;
        return (
          <div key={i} className="mb-3 p-2 border-bottom border-light">
            <div className="row g-2 align-items-end">
              <div className="col-12 col-md-3">
                <Field
                  name={`${props.name}[${i}].ServiceID`}
                  label="Service"
                  placeholder="Select Service"
                  options={b2bServiceOptions}
                  component={FormSelectionField}
                  onChange={(e: any) => {
                    const val = e.value;
                    props.onFormChange(`${props.name}[${i}].ServiceID`, { value: val });
                    const opt = b2bServiceOptions.find((o: any) => o.value == val);
                    if (opt) props.onFormChange(`${props.name}[${i}].ServiceName`, { value: opt.label });
                  }}
                />
              </div>
              <div className="col-6 col-md-2">
                <Field name={`${props.name}[${i}].TotalPrice`} label="Total Price" placeholder="INR" component={FormNumericTextField} />
              </div>
              <div className="col-6 col-md-2">
                <Field name={`${props.name}[${i}].Day`} label="Day" placeholder="Select Day" options={dayOptions} component={FormSelectionField} />
              </div>
              <div className="col-10 col-md-4">
                <Field
                  name={`${props.name}[${i}].Comment`}
                  label="Comment"
                  placeholder="Notes..."
                  component={FormTextField}
                />
              </div>
              <div className="col-2 col-md-1 text-center pb-2">
                <div className="b2b-remove-icon shadow-sm" onClick={() => onRemove({ index: i })} title="Remove">
                  <IconComponent icon={FaTimes} style={{ fontSize: "11px" }} />
                </div>
              </div>
            </div>
          </div>
        );
      })}
      <div className="mt-3">
        <Button
          type="button"
          fillMode="outline"
          themeColor="primary"
          className="fw-bold px-3"
          onClick={() => onPush({ value: { ServiceID: 0, TotalPrice: 0, Day: "", DayNo: 0, Comment: "" } })}
          style={{ fontSize: "13px", borderRadius: "6px" }}
        >
          <IconComponent icon={FaPlus} className="me-2" /> Add Service
        </Button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// TransportsAndActivitiesSection  (main export)
// ─────────────────────────────────────────────────────────────────────────────
const TransportsAndActivitiesSection: React.FC<TransportsAndActivitiesSectionProps> = ({
  values,
  valueGetter,
  onFormChange,
  dayOptions,
}) => {
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    dispatch(getAllActiveServiceLocation());
    dispatch(getAllActiveTravelActivity());
    dispatch(getAllActiveVehicleTypes());
    dispatch(getAllActiveB2BServices());
  }, [dispatch]);

  const activeServiceLocations = useAppSelector((state: any) => state.serviceLocation.ServiceLocationList || []);
  const activeTravelActivities = useAppSelector((state: any) => state.travelActivity.TravelActivityList || []);
  const activeVehicleTypes = useAppSelector((state: any) => state.vehicleType.VehicleTypeList || []);
  const activeB2BServices = useAppSelector((state: any) => state.b2bservice.B2BServiceList || []);

  // ✅ THE REAL FIX: compute total independently by walking valueGetter paths directly.
  // This completely bypasses the stale `values` snapshot and reads live field state.
  const displayedTotals = useLiveTransportTotal(valueGetter);

  // Handle Initial State
  React.useEffect(() => {
    if (!values?.TransportsAndActivities?.ServiceGroups?.length && onFormChange && dayOptions.length > 0) {
      const firstDay = dayOptions[0];
      const initialDaySet = [{ Day: firstDay.value, Date: firstDay.date }];
      onFormChange("TransportsAndActivities.ServiceGroups", {
        value: [{
          StayDays: initialDaySet,
          Services: [{
            Type: "TRANSPORT",
            ServiceID: '',
            ServiceTypeID: '',
            Subservices: [{ TypeID: '', Qty: 1, DailyPrices: [{ Date: firstDay.date, Rate: 0, GivenPrice: 0 }] }]
          }]
        }]
      });
    }
  }, [values?.TransportsAndActivities?.ServiceGroups, onFormChange, dayOptions]);

  return (
    <ShadowCard className="b2b-quotation-module b2b-card mb-4 p-0">
      <div className="p-4 d-flex gap-3 align-items-start border-bottom border-light">
        <div className="hotel-icon-wrap shadow-sm">
          <IconComponent icon={FaBus} />
        </div>
        <div className="flex-grow-1">
          <div className="d-flex align-items-center gap-2">
            <Typography.h5 className="section-title mb-0">Transports and Activities</Typography.h5>
          </div>
          <Typography.p className="section-subtitle mb-0 mt-1" style={{ fontSize: "13px" }}>
            Please add the transportation services and Activities (if included) details and the selling cost price for each service.
          </Typography.p>
          <div className="mt-2 text-muted opacity-75" style={{ fontSize: "11.5px" }}>
            <IconComponent icon={FaRegQuestionCircle} className="me-1" />
            Tip: To speed up the process of adding multiple services, use <b>Next Day</b> or <b>Duplicate</b> actions.
          </div>
        </div>
      </div>

      <div className="p-4">
        <FieldArray
          name="TransportsAndActivities.ServiceGroups"
          component={ServiceGroupsList}
          dayOptions={dayOptions}
          onFormChange={onFormChange}
          activeServiceLocations={activeServiceLocations}
          activeTravelActivities={activeTravelActivities}
          activeB2BServices={activeB2BServices}
          activeVehicleTypes={activeVehicleTypes}
          valueGetter={valueGetter}
        />

        <div className="mt-5 border-top pt-3">
          <Typography.h6 className="fw-bold text-dark mb-0" style={{ fontSize: "15px" }}>
            Any extra or sightseeing in transportation
          </Typography.h6>
          <Typography.p className="section-subtitle mb-3" style={{ fontSize: "12.5px" }}>
            Add any extra services like any side destination trip that is provided only per customer request
          </Typography.p>
          <FieldArray
            name="TransportsAndActivities.ExtraTransportationServices"
            component={ExtraServicesList}
            activeB2BServices={activeB2BServices}
            dayOptions={dayOptions}
            onFormChange={onFormChange}
          />
        </div>

        <div className="mt-5 d-flex justify-content-end">
          <div className="live-totals-footer">
            {/* Row 1: Total */}
            <div className="total-row">
              <span className="label">
                Total:
              </span>
              <span className="value">
                <span className="currency">INR</span>
                {displayedTotals.overallTotal.toLocaleString()}
              </span>
            </div>

            {/* Row 2: Breakdown */}
            <div className="breakdown-row">
              <div className="item">
                <span>Transports:</span>
                <span className="unit">INR</span>
                <b>{displayedTotals.transportTotal.toLocaleString()}</b>
              </div>
              <span className="sep">•</span>
              <div className="item">
                <span>Activity:</span>
                <span className="unit">INR</span>
                <b>{displayedTotals.activityTotal.toLocaleString()}</b>
              </div>
              <span className="sep">•</span>
              <div className="item">
                <span>Others:</span>
                <span className="unit">INR</span>
                <b>{displayedTotals.extraTotal.toLocaleString()}</b>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ShadowCard>
  );
};

export default memo(TransportsAndActivitiesSection);